import * as React from 'react';
import { observer } from 'mobx-react';
import { runInAction } from 'mobx';
import If from '../If/If';
import InputWrapper from '../Inputs/InputWrapper';
import * as uuid from 'uuid';
import { DisplayType } from '../Models/Enums';
import InputsHelper from '../Helpers/InputsHelper';
import { BoundProp, getBoundValue, setBoundValue } from '../Inputs/BoundModelProperty';

export interface ITextFieldProps<T> {
	model: T;
	/**
	 * `string | number` rather than just `string` because `NumberTextField` renders a
	 * `TextField` over the caller's number-typed property (see `NumberTextBox.tsx`), which is
	 * also why the rendered value below has to tolerate a numeric `0`.
	 */
	modelProperty: BoundProp<T, string | number>;
	id?: string;
	name?: string;
	className?: string;
	inputClassName?: string;
	defaultToUndefined?: boolean;
	displayType?: DisplayType;
	label?: React.ReactNode;
	labelVisible?: boolean;
	isRequired?: boolean;
	isDisabled?: boolean;
	isReadOnly?: boolean;
	staticInput?: boolean;
	tooltip?: string;
	subDescription?: string;
	inputProps?: React.InputHTMLAttributes<HTMLInputElement> & { 'data-test-id'?: string };
	placeholder?: string;
	clickToClear?: boolean;
	autoFocus?: boolean;
	errors?: string | string[];
	onAfterChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onChangeAndBlur?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onClickToClear?: (event: React.MouseEvent<HTMLButtonElement>) => void;
	sideButton?: React.ReactNode;
	addToEndOfValue?: string;
	addToStartOfValue?: string;
}

class TextFieldComponent<T> extends React.Component<ITextFieldProps<T>, any> {
	public static defaultProps = {
		clickToClear: false,
		inputProps: {},
		className: '',
	};

	private _input?: HTMLInputElement;
	private uuid = uuid.v4();
	private valueWhenFocused: string = '';

	private onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		// Need to disable the rule here since we change the type which is incompatible with destructuring
		// eslint-disable-next-line prefer-destructuring
		let value: string | undefined = event.target.value;
		value = value === '' && this.props.defaultToUndefined ? undefined : value;

		runInAction(() => {
			setBoundValue(this.props.model, this.props.modelProperty, value);
		});

		if (this.props.onAfterChange) {
			this.props.onAfterChange(event);
		}
	};

	private onFocus = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.valueWhenFocused = event.target.value;
	};

	private onBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (this.valueWhenFocused !== event.target.value && this.props.onChangeAndBlur) {
			this.props.onChangeAndBlur(event);
		}
	};

	private onClickToClear = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (this.props.onClickToClear) {
			return this.props.onClickToClear(event);
		}
		runInAction(() => {
			setBoundValue(this.props.model, this.props.modelProperty, '');
		});
	};

	componentDidMount() {
		const { autoFocus } = this.props;

		if (autoFocus && this._input) {
			this._input.focus();
		}
	}

	public render() {
		const {
			model,
			modelProperty,
			name,
			className,
			id = this.uuid,
			displayType,
			label,
			labelVisible = true,
			isRequired,
			isDisabled,
			isReadOnly,
			staticInput,
			tooltip,
			subDescription,
			clickToClear,
			placeholder,
			errors,
			inputClassName,
			sideButton,
			inputProps,
			addToEndOfValue,
			addToStartOfValue,
		} = this.props;

		const fieldId = `${id}-field`;
		const ariaLabel = InputsHelper.getAriaLabel(label, labelVisible);
		const boundValue = getBoundValue(model, modelProperty);
		const ariaDescribedby = InputsHelper.getAriaDescribedBy(id, tooltip, subDescription);

		return (
			<InputWrapper
				id={id}
				inputId={fieldId}
				className={className}
				displayType={displayType}
				isRequired={isRequired}
				staticInput={staticInput}
				tooltip={tooltip}
				subDescription={subDescription}
				label={label}
				labelVisible={labelVisible}
				errors={errors}
				sideButton={sideButton}
			>
				<input
					className={inputClassName}
					type="text"
					name={name}
					id={fieldId}
					value={
						(addToStartOfValue ?? '') +
						(boundValue || boundValue === 0 ? boundValue : '') +
						(addToEndOfValue ?? '')
					}
					onChange={this.onChange}
					onBlur={this.onBlur}
					onFocus={this.onFocus}
					placeholder={placeholder ?? ariaLabel}
					disabled={isDisabled}
					readOnly={staticInput || isReadOnly}
					aria-label={ariaLabel}
					aria-describedby={ariaDescribedby}
					ref={i => {
						this._input = i ?? undefined;
					}}
					{...inputProps}
				/>
				<If condition={clickToClear}>
					<button
						className="click-to-clear icon-cross icon-right"
						onClick={this.onClickToClear}
						type="button"
						aria-label="Click to clear"
					/>
				</If>
			</InputWrapper>
		);
	}
}

export const TextField = observer(TextFieldComponent);
